import RootNavigator from "@/navigation/RootNavigator";
import { themeConfig } from "@/theme/theme";
import { Analytics } from "@/utils/analytics";
import initCrashlytics from "@/utils/crashlytics";
import "@/utils/disableFontScaling";
import { useGetAppSettingsQuery } from "@invesco/shared/api/appSettings";
import { initialiseAPI } from "@invesco/shared/api/request";
import store from "@invesco/shared/store";
import { useAppSelector } from "@invesco/shared/store/index";
import { initialiseEncryption } from "@invesco/shared/utils/encryption";
import { ThemeProvider } from "@rneui/themed";
import { useFonts } from "expo-font";
import React, { useEffect } from "react";
import { NativeModules, Platform, StyleSheet, View } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { fetch as fetchSslPinning } from "react-native-ssl-pinning";
import { Provider } from "react-redux";

const EXPO_PUBLIC_AES_KEY = process.env.EXPO_PUBLIC_AES_KEY;
const EXPO_PUBLIC_OLD_AES_KEY = process.env.EXPO_PUBLIC_OLD_AES_KEY;
const EXPO_PUBLIC_ENV = process.env.EXPO_PUBLIC_ENV;
const EXPO_PUBLIC_API_URL = process.env.EXPO_PUBLIC_API_URL;
const EXPO_PUBLIC_ENABLE_ENCRYPTION = process.env.EXPO_PUBLIC_ENABLE_ENCRYPTION;
const PUBLIC_SSL_PINNING_STATE = process.env.PUBLIC_SSL_PINNING_STATE;

const env = EXPO_PUBLIC_ENV || "DEV";
const isWeb = Platform.OS === "web";
const baseUrl = isWeb ? "/dev-api/" : (EXPO_PUBLIC_API_URL ?? "");
const { RNSslPinning } = NativeModules;
const sslPinningOptions = Platform.select({
  android: {
    certs: ["sha256/k5+JfFSlThXYs4rCK+cJVuBjwx4+J3sOU8Dyy/CwcG0="],
  },
  ios: {
    certs: ["invesco_uat_cert"],
  },
});
const isSslPinningEnabled = PUBLIC_SSL_PINNING_STATE === "ENABLED";

initialiseAPI({
  baseUrl,
  env: env as "DEV" | "PROD",
  applicationType: isWeb ? "Web" : "Mobile",
  enableEncryption: EXPO_PUBLIC_ENABLE_ENCRYPTION === "true",
  fetchApi: !isWeb && RNSslPinning && isSslPinningEnabled ? fetchSslPinning : undefined,
  sslOptions: sslPinningOptions,
  eventLogger: Analytics.logEvent,
});

initialiseEncryption({
  aesKey: EXPO_PUBLIC_AES_KEY ?? "",
  oldAesKey: EXPO_PUBLIC_OLD_AES_KEY ?? "",
});

// Microsoft Clarity — initialise for native platforms only.
// Web already has the Clarity snippet in apps/mobile/web/index.html (project: ypddwx9p3w).
// Using require() instead of a top-level import so Metro loads the module lazily:
// the package registers native bindings at load time and throws Invariant Violation
// in Expo Go before any call is made. A lazy require inside try-catch silently skips
// it in Expo Go and runs correctly in EAS builds where the native module is present.
if (Platform.OS !== "web") {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { initialize, LogLevel } = require(/* webpackIgnore: true */ "@microsoft/react-native-clarity");
    initialize("yncdbhjqhv", {
      // Use LogLevel.Verbose while testing to debug in the Clarity dashboard.
      // Switch back to None for UAT / CUG / production builds.
      logLevel: LogLevel.None,
    });
  } catch {
    // Native module unavailable (Expo Go) — silently skip.
    // Clarity will work correctly in EAS preview / UAT / production builds.
  }
}

// Global Font Configuration
const applyGlobalFont = () => {
  if (Platform.OS === "web") {
    // Safe handling for Web: Inject global CSS
    const style = document.createElement("style");
    style.textContent = `
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        overflow: hidden;
        overscroll-behavior: none;
      }
      body, input, textarea, select, button {
        font-family: "Graphik-Regular" !important;
      }
      #root {
        display: flex;
        height: 100%;
        flex-direction: column;
      }
    `;
    document.head.appendChild(style);
  }
  // Note: Font scaling is disabled globally via disableFontScaling.ts
  // Font family for native is applied via the global font patch in that module
};

const initialMetrics =
  Platform.OS === "web"
    ? {
        frame: { x: 0, y: 0, width: 0, height: 0 },
        insets: { top: 0, left: 0, right: 0, bottom: 0 },
      }
    : undefined;

const ThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  const { theme: themeSelection } = useAppSelector((state) => state.user);
  const selectedTheme = {
    ...themeConfig,
    mode: themeSelection,
    colors: themeSelection === "dark" ? themeConfig.darkColors : themeConfig.lightColors,
  };

  return <ThemeProvider theme={selectedTheme}>{children}</ThemeProvider>;
};

// Fetches the public app-settings once at app start and keeps a top-level
// subscription alive for the whole session, so loader promo text is fetched a
// single time and served from cache to every ScreenLoader. Renders nothing.
const AppSettingsPrefetch = () => {
  useGetAppSettingsQuery();
  return null;
};

export default function App() {
  const [fontsLoaded] = useFonts({
    "Graphik-Light": require("./assets/fonts/Graphik-Light.otf"),
    "Graphik-Medium": require("./assets/fonts/Graphik-Medium.otf"),
    "Graphik-Regular": require("./assets/fonts/Graphik-Regular.otf"),
    "Graphik-Semibold": require("./assets/fonts/Graphik-Semibold.otf"),
  });

  useEffect(() => {
    if (fontsLoaded) {
      applyGlobalFont();
    }
  }, [fontsLoaded]);

  useEffect(() => {
    initCrashlytics();
  }, []);

  if (!fontsLoaded) return null;

  return (
    <Provider store={store}>
      <AppSettingsPrefetch />
      <GestureHandlerRootView style={{ flex: 1 }}>
        <ThemeWrapper>
          <SafeAreaProvider initialMetrics={initialMetrics}>
            <View style={styles.container}>
              <RootNavigator />
            </View>
          </SafeAreaProvider>
        </ThemeWrapper>
      </GestureHandlerRootView>
    </Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    alignItems: "stretch",
    justifyContent: "flex-start",
  },
});
